/**
 * SEP 5.d — SaleV2.purchase + receipt handling.
 *
 * Boundary contract:
 *   - ONE new authorized write on-chain call: `SaleV2.purchase(x, y, w, h,
 *     maxTotalPrice, deadline)`. Signature verified from the FROZEN
 *     contract source at `contracts/src/TheGridSaleV2.sol` L328.
 *   - `maxTotalPrice` == EXACT total_price_microusdc of the FINAL forced
 *     re-quote fetched at click time. No slippage, no local pricing,
 *     no pre-approve total, no allowance/balance mix-in.
 *   - `deadline`   == floor(nowUnixSeconds()) + 300 seconds, computed
 *     IMMEDIATELY before the wallet write (§J/§K).
 *   - `address`    == `config.sale_contract_address` at write time.
 *   - `chainId`    == `config.chain_id` at write time.
 *   - No optimistic parcel insertion. Success signal = tx_confirmed_onchain
 *     ONLY. Backend indexing polling belongs to 5.e.
 *   - Fires ONLY on explicit user click on the "Purchase" button.
 *
 * SPLIT (testability, same shape as 5.c):
 *   - `SALEV2_PURCHASE_ABI` — minimal ABI, `purchase()` only.
 *   - `PURCHASE_DEADLINE_SECONDS` + `computePurchaseDeadline(now)` — pure.
 *   - `derivePurchaseEligibility()` — pure gate for the pre-click button state.
 *   - `PurchaseController` — pure state machine with injected deps.
 *   - `useSaleV2Purchase()` — thin wagmi/viem wiring around the controller.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { useWriteContract, usePublicClient } from "wagmi";
import type { Abi } from "viem";

import type { GridSelection } from "./grid-selection";
import { selectionsEqual } from "./grid-selection";
import { isUserRejection } from "./usdc-approve";
import type { FinancialReadContext } from "./usdc-reads";
import type { FinancialReadiness } from "./financial-readiness";
import type { SaleConfig, SaleQuote, SaleStatus } from "./sale-types";

// ---------------------------------------------------------------------
// Minimal SaleV2 WRITE ABI — purchase() ONLY.
// Signature from the FROZEN contract source (contracts/src/TheGridSaleV2.sol L328).
// ---------------------------------------------------------------------
export const SALEV2_PURCHASE_ABI = [
  {
    type: "function",
    name: "purchase",
    stateMutability: "nonpayable",
    inputs: [
      { name: "x", type: "uint16" },
      { name: "y", type: "uint16" },
      { name: "w", type: "uint16" },
      { name: "h", type: "uint16" },
      { name: "maxTotalPrice", type: "uint256" },
      { name: "deadline", type: "uint256" },
    ],
    outputs: [{ name: "parcelId", type: "uint256" }],
  },
] as const satisfies Abi;

// ---------------------------------------------------------------------
// Deadline policy (§J/§K)
// ---------------------------------------------------------------------
export const PURCHASE_DEADLINE_SECONDS = 300;

/** Clock abstraction — inject a stubbed one from tests. */
export type NowUnixSeconds = () => number;

export const defaultNowUnixSeconds: NowUnixSeconds = () =>
  Math.floor(Date.now() / 1000);

/**
 * §J/§K — compute deadline = floor(now) + 300 as a bigint.
 * MUST be called IMMEDIATELY before the wallet write, AFTER the final
 * forced re-quote. Never at mount, never at selection, never at approve.
 */
export function computePurchaseDeadline(now: NowUnixSeconds = defaultNowUnixSeconds): bigint {
  const n = now();
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
    throw new Error("computePurchaseDeadline: clock returned invalid seconds");
  }
  return BigInt(n) + BigInt(PURCHASE_DEADLINE_SECONDS);
}

// ---------------------------------------------------------------------
// uint16 safety (§I)
// ---------------------------------------------------------------------
export function isUint16SafeSelection(sel: GridSelection): boolean {
  const { x, y, w, h } = sel;
  if (!Number.isInteger(x) || !Number.isInteger(y) || !Number.isInteger(w) || !Number.isInteger(h)) {
    return false;
  }
  if (x < 0 || y < 0 || w < 1 || h < 1) return false;
  if (x > 999 || y > 999) return false;
  if (x + w > 1000 || y + h > 1000) return false;
  // Cast bounds redundantly for clarity.
  if (x > 65535 || y > 65535 || w > 65535 || h > 65535) return false;
  return true;
}

// ---------------------------------------------------------------------
// Snapshot (§F) — immutable authorization record
// ---------------------------------------------------------------------
export interface PurchaseSnapshot {
  chainId: number;
  owner: `0x${string}`;
  saleAddress: `0x${string}`;
  paymentToken: `0x${string}`;
  selection: GridSelection;
  maxTotalPrice: bigint;
  quoteAsOfBlock: number;
  deadline: bigint;
}

// ---------------------------------------------------------------------
// Purchase state machine
// ---------------------------------------------------------------------
export type PurchaseState =
  | { kind: "idle" }
  | { kind: "refreshing_quote" }
  | { kind: "awaiting_wallet"; snapshot: PurchaseSnapshot }
  | { kind: "submitted"; snapshot: PurchaseSnapshot; txHash: `0x${string}` }
  | { kind: "confirming"; snapshot: PurchaseSnapshot; txHash: `0x${string}` }
  | { kind: "confirmed_onchain"; snapshot: PurchaseSnapshot; txHash: `0x${string}` }
  | { kind: "rejected"; snapshot: PurchaseSnapshot }
  | {
      kind: "reverted";
      snapshot: PurchaseSnapshot;
      txHash: `0x${string}`;
      revertReason?: string;
    }
  | {
      kind: "error";
      snapshot?: PurchaseSnapshot;
      error: string;
      txHash?: `0x${string}`;
    };

export type PurchaseStateKind = PurchaseState["kind"];

// ---------------------------------------------------------------------
// Eligibility gate (§M) — button visible + click authorized
// ---------------------------------------------------------------------
export interface PurchaseEligibilityInputs {
  readCtx: FinancialReadContext | null;
  quote: SaleQuote | null;
  readiness: FinancialReadiness | null;
  selection: GridSelection | null;
}

export type PurchaseIneligibilityReason =
  | "read_context_missing"
  | "no_selection"
  | "no_quote"
  | "quote_not_v2"
  | "quote_not_sellable"
  | "quote_price_missing"
  | "readiness_missing"
  | "readiness_approval_required"
  | "readiness_insufficient_balance"
  | "selection_not_uint16_safe";

export type PurchaseEligibility =
  | { eligible: true }
  | { eligible: false; reason: PurchaseIneligibilityReason };

export function derivePurchaseEligibility(
  i: PurchaseEligibilityInputs,
): PurchaseEligibility {
  if (i.readCtx == null) return { eligible: false, reason: "read_context_missing" };
  if (i.selection == null) return { eligible: false, reason: "no_selection" };
  if (!isUint16SafeSelection(i.selection)) {
    return { eligible: false, reason: "selection_not_uint16_safe" };
  }
  if (i.quote == null) return { eligible: false, reason: "no_quote" };
  if (i.quote.generation !== "v2") return { eligible: false, reason: "quote_not_v2" };
  if (!i.quote.sellable) return { eligible: false, reason: "quote_not_sellable" };
  if (i.quote.total_price_microusdc == null || i.quote.total_price_microusdc <= 0n) {
    return { eligible: false, reason: "quote_price_missing" };
  }
  if (i.readiness == null) return { eligible: false, reason: "readiness_missing" };
  if (i.readiness.kind === "approval_required") {
    return { eligible: false, reason: "readiness_approval_required" };
  }
  if (i.readiness.kind === "insufficient_balance") {
    return { eligible: false, reason: "readiness_insufficient_balance" };
  }
  return { eligible: true };
}

// ---------------------------------------------------------------------
// Context drift guards (§X pre-write, §Y post-write)
// ---------------------------------------------------------------------
export interface CurrentPurchaseContext {
  chainId: number;
  owner: `0x${string}`;
  saleAddress: `0x${string}`;
  paymentToken: `0x${string}`;
  selection: GridSelection | null;
}

/**
 * §X — before the wallet write, verify the CURRENT UI context still
 * matches the freshly-built snapshot. Any mismatch means the user's
 * consent no longer applies to the current state.
 */
export function isSnapshotStillCurrent(
  snap: PurchaseSnapshot,
  cur: CurrentPurchaseContext,
): boolean {
  if (snap.chainId !== cur.chainId) return false;
  if (snap.owner.toLowerCase() !== cur.owner.toLowerCase()) return false;
  if (snap.saleAddress.toLowerCase() !== cur.saleAddress.toLowerCase()) return false;
  if (snap.paymentToken.toLowerCase() !== cur.paymentToken.toLowerCase()) return false;
  if (cur.selection == null) return false;
  if (!selectionsEqual(snap.selection, cur.selection)) return false;
  return true;
}

// ---------------------------------------------------------------------
// Controller (pure, injectable deps)
// ---------------------------------------------------------------------
export interface PurchaseWriteParams {
  address: `0x${string}`;
  abi: typeof SALEV2_PURCHASE_ABI;
  functionName: "purchase";
  args: readonly [number, number, number, number, bigint, bigint];
  chainId: number;
}

export interface PurchaseReceiptOutcome {
  status: "success" | "reverted";
  revertReason?: string;
}

export interface PurchaseDeps {
  writeContract: (params: PurchaseWriteParams) => Promise<`0x${string}`>;
  waitForReceipt: (hash: `0x${string}`) => Promise<PurchaseReceiptOutcome>;
  onStateChange?: (state: PurchaseState) => void;
}

export class PurchaseController {
  private _state: PurchaseState = { kind: "idle" };

  constructor(private deps: PurchaseDeps) {}

  get state(): PurchaseState {
    return this._state;
  }

  get isPending(): boolean {
    return (
      this._state.kind === "refreshing_quote" ||
      this._state.kind === "awaiting_wallet" ||
      this._state.kind === "submitted" ||
      this._state.kind === "confirming"
    );
  }

  reset(): void {
    if (this.isPending) return;
    this.setState({ kind: "idle" });
  }

  /** Enter "refreshing_quote" gate; caller must fetch the fresh quote. */
  beginRefreshQuote(): void {
    if (this.isPending) return;
    // §5.d.1 audit — terminal duplicate-write guards mirror `purchase()`:
    //   - confirmed_onchain: another attempt would double-buy the parcel.
    //   - error+txHash: on-chain outcome UNKNOWN → another attempt could
    //     double-spend. Both require explicit reset() before a new attempt.
    if (this._state.kind === "confirmed_onchain") return;
    if (this._state.kind === "error" && this._state.txHash != null) return;
    this.setState({ kind: "refreshing_quote" });
  }

  /** Fail the refresh step from the caller (e.g. quote stale/failed). */
  failRefresh(reason: string): void {
    if (this._state.kind !== "refreshing_quote") return;
    this.setState({ kind: "error", error: reason });
  }

  private setState(next: PurchaseState) {
    this._state = next;
    this.deps.onStateChange?.(next);
  }

  /**
   * Fire the SaleV2.purchase write. Snapshot must be built by the caller
   * AFTER the forced final re-quote + revalidation + drift check. The
   * controller does NOT re-validate the snapshot — that is the caller's
   * consent gate.
   *
   * Reentrancy: double-clicks during pending are silent no-ops.
   */
  async purchase(snap: PurchaseSnapshot): Promise<void> {
    // Only allowed to progress from `refreshing_quote` OR `idle`.
    if (this.isPending && this._state.kind !== "refreshing_quote") return;
    // §5.d.1 audit — terminal duplicate-write guards:
    //   - confirmed_onchain: another write would double-buy the same parcel.
    //   - error+txHash: waitForReceipt threw AFTER the wallet submitted the
    //     tx, so the on-chain outcome is UNKNOWN — a second write could
    //     double-spend. Both require an explicit `reset()` before another
    //     purchase attempt.
    if (this._state.kind === "confirmed_onchain") return;
    if (this._state.kind === "error" && this._state.txHash != null) return;
    if (
      this._state.kind !== "refreshing_quote" &&
      this._state.kind !== "idle" &&
      this._state.kind !== "rejected" &&
      this._state.kind !== "reverted" &&
      this._state.kind !== "error"
    ) {
      return;
    }

    // Defensive runtime invariants (belt-and-braces around the caller's gate).
    if (!isUint16SafeSelection(snap.selection)) {
      this.setState({ kind: "error", snapshot: snap, error: "selection_not_uint16_safe" });
      return;
    }
    if (typeof snap.maxTotalPrice !== "bigint" || snap.maxTotalPrice <= 0n) {
      this.setState({ kind: "error", snapshot: snap, error: "invalid_max_total_price" });
      return;
    }
    if (typeof snap.deadline !== "bigint" || snap.deadline <= 0n) {
      this.setState({ kind: "error", snapshot: snap, error: "invalid_deadline" });
      return;
    }

    this.setState({ kind: "awaiting_wallet", snapshot: snap });

    let hash: `0x${string}`;
    try {
      hash = await this.deps.writeContract({
        address: snap.saleAddress,
        abi: SALEV2_PURCHASE_ABI,
        functionName: "purchase",
        args: [
          snap.selection.x,
          snap.selection.y,
          snap.selection.w,
          snap.selection.h,
          snap.maxTotalPrice,
          snap.deadline,
        ] as const,
        chainId: snap.chainId,
      });
    } catch (err) {
      if (isUserRejection(err)) {
        this.setState({ kind: "rejected", snapshot: snap });
      } else {
        this.setState({
          kind: "error",
          snapshot: snap,
          error: err instanceof Error ? err.message : String(err),
        });
      }
      return;
    }

    this.setState({ kind: "submitted", snapshot: snap, txHash: hash });
    this.setState({ kind: "confirming", snapshot: snap, txHash: hash });

    try {
      const rc = await this.deps.waitForReceipt(hash);
      if (rc.status === "success") {
        this.setState({ kind: "confirmed_onchain", snapshot: snap, txHash: hash });
      } else {
        this.setState({
          kind: "reverted",
          snapshot: snap,
          txHash: hash,
          revertReason: rc.revertReason,
        });
      }
    } catch (err) {
      this.setState({
        kind: "error",
        snapshot: snap,
        txHash: hash,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
}

// ---------------------------------------------------------------------
// §5.d.2 — preparePurchase: fresh authoritative preflight
// ---------------------------------------------------------------------
/**
 * §5.d.2 authoritative pre-write preflight. Runs AFTER the forced final
 * `/sale/quote` and BEFORE the SaleV2.purchase wallet write.
 *
 * SAFETY CONTRACT:
 *   - All financial state (balance, allowance) MUST come from the RETURN
 *     values of the injected fetchers — never from React hook caches.
 *     The caller wires publicClient.readContract results in.
 *   - Config/status MUST be freshly fetched from the backend at this
 *     exact moment via the injected fetchers — closure-captured
 *     mount-time values are NOT authoritative here.
 *   - Any fetcher failure OR unusable/undefined value → FAIL CLOSED with
 *     a specific abort reason. No optimistic assumptions.
 *   - Identity of the sale/config/token is compared against click-time
 *     values captured by the caller. A drift aborts the write; the user
 *     must re-click (5.d does NOT auto-migrate to a new contract).
 *
 * The function is deliberately PURE + injectable so the tests can drive
 * every deterministic branch without any wagmi/react wiring.
 */
export interface PurchasePreflightClickCtx {
  chainId: number;
  owner: `0x${string}`;
  saleAddress: `0x${string}`;
  paymentToken: `0x${string}`;
}

export interface PurchasePreflightInputs {
  clickCtx: PurchasePreflightClickCtx;
  selection: GridSelection;
  /** Already-race-guarded FINAL quote from QuoteLoader.load(sel). */
  freshQuote: SaleQuote;
}

export interface PurchasePreflightDeps {
  fetchFreshConfig: () => Promise<SaleConfig>;
  fetchFreshStatus: () => Promise<SaleStatus>;
  fetchFreshBalance: () => Promise<bigint>;
  fetchFreshAllowance: () => Promise<bigint>;
  now?: NowUnixSeconds;
}

export type PurchasePreflightAbortReason =
  | "final_quote_not_sellable"
  | "fresh_read_failed"
  | "fresh_config_unavailable"
  | "fresh_status_unavailable"
  | "fresh_balance_unavailable"
  | "fresh_allowance_unavailable"
  | "fresh_config_not_v2"
  | "fresh_status_not_v2"
  | "fresh_status_not_operational"
  | "fresh_status_paused"
  | "fresh_chain_id_mismatch"
  | "fresh_sale_address_mismatch"
  | "fresh_payment_token_mismatch"
  | "insufficient_balance_post_requote"
  | "approval_required_post_requote"
  | "selection_not_uint16_safe"
  | "context_drift_pre_write";

export type PurchasePreflightResult =
  | { kind: "ready"; snapshot: PurchaseSnapshot }
  | { kind: "abort"; reason: PurchasePreflightAbortReason };

export async function preparePurchase(
  inputs: PurchasePreflightInputs,
  deps: PurchasePreflightDeps,
): Promise<PurchasePreflightResult> {
  const { clickCtx, selection, freshQuote } = inputs;

  // Fresh quote invariants — required BEFORE any RPC work.
  if (
    freshQuote.generation !== "v2" ||
    !freshQuote.sellable ||
    freshQuote.total_price_microusdc == null
  ) {
    return { kind: "abort", reason: "final_quote_not_sellable" };
  }
  const freshTotal = freshQuote.total_price_microusdc;

  // Parallel fresh reads. Any rejection fails closed.
  let freshConfig: SaleConfig;
  let freshStatus: SaleStatus;
  let freshBalance: bigint;
  let freshAllowance: bigint;
  try {
    const [cfg, st, bal, allow] = await Promise.all([
      deps.fetchFreshConfig(),
      deps.fetchFreshStatus(),
      deps.fetchFreshBalance(),
      deps.fetchFreshAllowance(),
    ]);
    freshConfig = cfg;
    freshStatus = st;
    freshBalance = bal;
    freshAllowance = allow;
  } catch {
    return { kind: "abort", reason: "fresh_read_failed" };
  }

  // Unusable/undefined return values → fail closed on the exact reason.
  if (freshConfig == null) return { kind: "abort", reason: "fresh_config_unavailable" };
  if (freshStatus == null) return { kind: "abort", reason: "fresh_status_unavailable" };
  if (typeof freshBalance !== "bigint") {
    return { kind: "abort", reason: "fresh_balance_unavailable" };
  }
  if (typeof freshAllowance !== "bigint") {
    return { kind: "abort", reason: "fresh_allowance_unavailable" };
  }

  // §C — 7 fresh config/status identity checks (order below matches the
  // spec bullet order: config-ready → v2 → operational → paused → chain →
  // sale address → payment token).
  if (freshConfig.generation !== "v2") {
    return { kind: "abort", reason: "fresh_config_not_v2" };
  }
  if (freshStatus.generation !== "v2") {
    return { kind: "abort", reason: "fresh_status_not_v2" };
  }
  if (freshStatus.state !== "ok") {
    return { kind: "abort", reason: "fresh_status_not_operational" };
  }
  if (freshStatus.paused) {
    return { kind: "abort", reason: "fresh_status_paused" };
  }
  if (freshConfig.chain_id !== clickCtx.chainId) {
    return { kind: "abort", reason: "fresh_chain_id_mismatch" };
  }
  if (
    freshConfig.sale_contract_address.toLowerCase() !==
    clickCtx.saleAddress.toLowerCase()
  ) {
    return { kind: "abort", reason: "fresh_sale_address_mismatch" };
  }
  if (
    freshConfig.payment_token.toLowerCase() !==
    clickCtx.paymentToken.toLowerCase()
  ) {
    return { kind: "abort", reason: "fresh_payment_token_mismatch" };
  }

  // §B — financial revalidation using the RETURN values (never hook data).
  if (freshBalance < freshTotal) {
    return { kind: "abort", reason: "insufficient_balance_post_requote" };
  }
  if (freshAllowance < freshTotal) {
    return { kind: "abort", reason: "approval_required_post_requote" };
  }

  // uint16 safety on the selection.
  if (!isUint16SafeSelection(selection)) {
    return { kind: "abort", reason: "selection_not_uint16_safe" };
  }

  // Deadline computed IMMEDIATELY before the write (§J).
  const deadline = computePurchaseDeadline(deps.now);

  // Immutable snapshot built from click-time identity + fresh totals.
  const snapshot: PurchaseSnapshot = {
    chainId: clickCtx.chainId,
    owner: clickCtx.owner,
    saleAddress: clickCtx.saleAddress,
    paymentToken: clickCtx.paymentToken,
    selection: { x: selection.x, y: selection.y, w: selection.w, h: selection.h },
    maxTotalPrice: freshTotal,
    quoteAsOfBlock: freshQuote.quote_as_of_block,
    deadline,
  };

  // Final belt-and-braces context invariant.
  if (
    !isSnapshotStillCurrent(snapshot, {
      chainId: clickCtx.chainId,
      owner: clickCtx.owner,
      saleAddress: clickCtx.saleAddress,
      paymentToken: clickCtx.paymentToken,
      selection,
    })
  ) {
    return { kind: "abort", reason: "context_drift_pre_write" };
  }

  return { kind: "ready", snapshot };
}

// ---------------------------------------------------------------------
// React hook — thin wagmi/viem wiring.
// ---------------------------------------------------------------------
export interface UseSaleV2PurchaseResult {
  state: PurchaseState;
  controller: PurchaseController;
  isPending: boolean;
  reset: () => void;
}

export function useSaleV2Purchase(): UseSaleV2PurchaseResult {
  const [state, setState] = useState<PurchaseState>({ kind: "idle" });
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();

  const depsRef = useRef<{
    writeContract: (p: PurchaseWriteParams) => Promise<`0x${string}`>;
    waitForReceipt: (h: `0x${string}`) => Promise<PurchaseReceiptOutcome>;
  }>({
    writeContract: async () => {
      throw new Error("wallet_not_ready");
    },
    waitForReceipt: async () => {
      throw new Error("public_client_not_ready");
    },
  });
  depsRef.current.writeContract = async (p) => {
    const hash = (await writeContractAsync({
      address: p.address,
      abi: p.abi,
      functionName: p.functionName,
      args: p.args as unknown as readonly [number, number, number, number, bigint, bigint],
      chainId: p.chainId,
    } as never)) as `0x${string}`;
    return hash;
  };
  depsRef.current.waitForReceipt = async (hash) => {
    if (publicClient == null) throw new Error("public_client_not_ready");
    const rc = await publicClient.waitForTransactionReceipt({ hash });
    return { status: rc.status === "success" ? "success" : "reverted" };
  };

  const controllerRef = useRef<PurchaseController | null>(null);
  if (controllerRef.current == null) {
    controllerRef.current = new PurchaseController({
      writeContract: (p) => depsRef.current.writeContract(p),
      waitForReceipt: (h) => depsRef.current.waitForReceipt(h),
      onStateChange: (s) => setState(s),
    });
  }

  const reset = useCallback(() => controllerRef.current!.reset(), []);
  const isPending = useMemo(
    () =>
      state.kind === "refreshing_quote" ||
      state.kind === "awaiting_wallet" ||
      state.kind === "submitted" ||
      state.kind === "confirming",
    [state.kind],
  );

  return { state, controller: controllerRef.current!, isPending, reset };
}
